import { UseFormReturn } from 'react-hook-form';
import { Validators } from '@helpers/constant';
import { TFunction } from 'i18next';
import { z } from 'zod';

export const defaultCreateFormValues = {
  name: '',
  nip: '',
  companyName: '',
  street: '',
  buildingNumber: '',
  apartmentNumber: '',
  zipCode: '',
  city: '',
  defaultCostCenter: false,
  automaticAssignment: false,
  useExistingTechnicalAccountNumber: false,
  assignedUsers: [],
  technicalAccountId: '',
  recipients: [],
};

export const defaultEditFormValues = {
  name: '',
  accountIban: '',
  nip: '',
  companyName: '',
  street: '',
  buildingNumber: '',
  apartmentNumber: '',
  zipCode: '',
  city: '',
  defaultCostCenter: false,
  automaticAssignment: false,
  assignedUsers: [],
  newDefaultCostCenterId: undefined,
  newAutomaticAssignmentCostCenterId: undefined,
  wasInitiallyDefault: false,
  wasInitiallyAutomaticAssignment: false,
  recipients: [],
};

export const createCostCenterFormSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(150, t('FORMS.MAX_CHARACTERS', { limit: 150 })),
      nip: z.string().min(1, t('FORMS.REQUIRED')).refine(Validators.NIP, t('FORMS.INVALID_NIP')),
      companyName: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(150, t('FORMS.MAX_CHARACTERS', { limit: 150 })),
      street: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
      buildingNumber: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 })),
      apartmentNumber: z
        .string()
        .trim()
        .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 }))
        .optional(),
      zipCode: z.string().min(6, t('FORMS.INVALID_ZIP_CODE')),
      city: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
      defaultCostCenter: z.boolean(),
      automaticAssignment: z.boolean(),
      useExistingTechnicalAccountNumber: z.boolean(),
      assignedUsers: z.array(z.string()),
      technicalAccountId: z.string(),
      recipients: z.array(z.string()),
    })
    .refine(
      (data) => {
        if (data.useExistingTechnicalAccountNumber) {
          return data.technicalAccountId !== '';
        }
        return true;
      },
      { message: t('FORMS.REQUIRED'), path: ['technicalAccountId'] },
    );

export type CreateCostCenterFormData = z.infer<ReturnType<typeof createCostCenterFormSchema>>;

export const editCostCenterFormSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(150, t('FORMS.MAX_CHARACTERS', { limit: 150 })),
      accountIban: z.string(),
      nip: z.string(),
      companyName: z.string(),
      street: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
      buildingNumber: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 })),
      apartmentNumber: z
        .string()
        .trim()
        .max(10, t('FORMS.MAX_CHARACTERS', { limit: 10 }))
        .optional(),
      zipCode: z.string().min(6, t('FORMS.INVALID_ZIP_CODE')),
      city: z
        .string()
        .trim()
        .min(1, t('FORMS.REQUIRED'))
        .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
      automaticAssignment: z.boolean(),
      assignedUsers: z.array(z.string()),
      defaultCostCenter: z.boolean(),
      newDefaultCostCenterId: z.string().optional(),
      wasInitiallyDefault: z.boolean(),
      wasInitiallyAutomaticAssignment: z.boolean(),
      newAutomaticAssignmentCostCenterId: z.string().optional(),
      recipients: z.array(z.string()),
    })
    .refine(
      (data) => {
        return data.wasInitiallyDefault && !data.defaultCostCenter
          ? data.newDefaultCostCenterId !== undefined
          : true;
      },
      {
        message: t('FORMS.REQUIRED'),
        path: ['newDefaultCostCenterId'],
      },
    )
    .refine(
      (data) => {
        return data.wasInitiallyAutomaticAssignment && !data.automaticAssignment
          ? data.newAutomaticAssignmentCostCenterId !== undefined
          : true;
      },
      { message: t('FORMS.REQUIRED'), path: ['newAutomaticAssignmentCostCenterId'] },
    );
export type EditCostCenterFormData = z.infer<ReturnType<typeof editCostCenterFormSchema>>;

export type CostCenterFormData = CreateCostCenterFormData | EditCostCenterFormData;

export type CostCenterFormType = UseFormReturn<CostCenterFormData>;
